<script setup lang="ts">
  defineOptions({
    name: 'CPRootContainer',
  })

  import { computed, defineAsyncComponent, ref, Teleport } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { useAuth } from '@common/auth-layer/composables/useAuth'
  import { Screens } from '@common/auth-layer/types'

  import { HIGHRISE_NOTIFICATION_CONFIG } from '@common/core-layer/constants'

  import { useLocation } from '@common/location-layer/composables/useLocation'

  import { usePortalTheme } from '@common/location-layer/composables/usePortalTheme'

  import { provideSeoSiteProfile } from '@common/core-layer/composables/seo/useSeo'

  import { useBrandedHead } from '@clientportal/routing-layer/composables/useBrandedHead'
  import { isCPPublicCheckoutRoutePath } from '@clientportal/routing-layer/constants/auth.constants'

  import { createElementId } from '@core/utils/core.utils'

  import { resolveCpBreadcrumbRegistryKey } from '@common/navigation-layer/config/cp-breadcrumb-registry'

  import { useCPBreadcrumbs } from '@common/navigation-layer/composables/useCPBreadcrumbs'

  import { useSidebarDrawer } from '@common/shared-ui-layer/composables/useSidebarDrawer'

  import { useAppOpenBanner } from '@common/shared-ui-layer/composables/useAppOpenBanner'

  import CustomHamburgerMenuIcon from '@common/shared-ui-layer/icons/CustomHamburgerMenuIcon.vue'

  import TopBar from '@common/shared-ui-layer/components/TopBar.vue'

  import TopBarMobile from '@common/shared-ui-layer/components/TopBarMobile.vue'

  import { ChevronLeftIcon, ChevronRightIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { HLContentWrap } from '@platform-ui/highrise'

  import CPBreadcrumb from '@ui/components/CPBreadcrumb.vue'

  import CPButton from '@ui/components/CPButton.vue'

  import CPText from '@ui/components/CPText.vue'

  import CPTooltip from '@ui/components/CPTooltip.vue'

  import { AccountSettingsTabKey } from '@layers/account-settings/common-layer/utils/account-settings-config'

  import { useAccountSettingsTabVisibility } from '@layers/account-settings/common-layer/composables/useAccountSettingsTabVisibility'

  import BetaPreviewBanner from '@common/beta-preview-layer/components/BetaPreviewBanner.vue'

  import CoursesAdminPreviewBanner from '@courses/admin-preview-layer/components/CoursesAdminPreviewBanner.vue'

  import { useCoursesAdminPreview } from '@courses/admin-preview-layer/composables/useCoursesAdminPreview'

  import AnnouncementsTopBarButton from '@courses/announcements-layer/components/AnnouncementsTopBarButton.vue'
  import { useCourseCustomCode } from '@courses/product-theme-layer/composables/useCourseCustomCode'

  import { isEventsDirectoryRoute } from '@clientportal/cp-events-layer/composables/useEventsSearchContext'
  import { useExhibitorAdminContext } from '@clientportal/cp-events-layer/composables/useExhibitorAdminContext'
  import { useCPSupportChatWidget } from '@clientportal/support-chat-layer/composables/useCPSupportChatWidget'

  import { useGroupCustomCode } from '@communities/groups-layer/composables/useGroupCustomCode'

  import { useEventsDetailBreadcrumbLabel } from '@clientportal/cp-events-layer/composables/useEventsDetailBreadcrumbLabel'
  import { createClientPortalSeoSiteProfile } from '../seo/clientPortalSeoProfile'
  import { useCPCommunitiesGroupBreadcrumbContext } from '../composables/useCPCommunitiesGroupBreadcrumb'

  import { useCPCoursesProductBreadcrumbContext } from '../composables/useCPCoursesProductBreadcrumb'

  import { useCPRouteContext } from '../composables/useCPRouteContext'

  import { useClientPortalAuthStateWatcher } from '../composables/useClientPortalAuthStateWatcher'

  import { useCPCustomCode } from '../composables/useCPCustomCode'

  import { useCPChatShell } from '../composables/useCPChatShell'

  import { useCPAppOpenBanner } from '../composables/useCPAppOpenBanner'

  import { useCPRootVisibility } from '../composables/useCPRootVisibility'

  import { useActiveSearchContext } from '../composables/useActiveSearchContext'

  import { useCPSwitcherSidebar } from '../composables/useCPSwitcherSidebar'

  import { usePortalSidebar } from '../composables/usePortalSidebar'
  import { useNotificationItemActionRegistry } from '../composables/useNotificationItemActionRegistry'

  import { useCPJoinedGroupsListPrefetch } from '../composables/useCPJoinedGroupsListPrefetch'

  import CPPortalSidebar from './CPPortalSidebar.vue'

  import CPPortalSidebarMobileDrawer from './CPPortalSidebarMobileDrawer.vue'

  const LoginModal = defineAsyncComponent(() => import('@clientportal/auth-layer/components/LoginModal.vue'))

  const AppOpenBanner = defineAsyncComponent(() => import('@common/shared-ui-layer/components/AppOpenBanner.vue'))

  const ChatContainer = defineAsyncComponent(() => import('@communities/chats-layer/components/organisms/ChatContainer.vue'))

  const ChatErrorToaster = defineAsyncComponent(() => import('@communities/chats-layer/components/organisms/ChatErrorToaster.vue'))

  const SearchWidget = defineAsyncComponent(() => import('@common/search-layer/components/SearchWidget.vue'))

  const MyCoursesNavbarSearch = defineAsyncComponent(() => import('@clientportal/cp-courses-layer/components/MyCoursesNavbarSearch.vue'))
  const EventsDirectoryNavbarSearch = defineAsyncComponent(
    () => import('@clientportal/cp-events-layer/components/molecules/EventsDirectoryNavbarSearch.vue')
  )
  const ExhibitorCenterTopBarToggle = defineAsyncComponent(
    () => import('@clientportal/cp-events-layer/components/molecules/ExhibitorCenterTopBarToggle.vue')
  )
  const ExhibitorCenterMobileNavEntry = defineAsyncComponent(
    () => import('@clientportal/cp-events-layer/components/molecules/ExhibitorCenterMobileNavEntry.vue')
  )

  const DiscoverNavbarSearch = defineAsyncComponent(() => import('@clientportal/discover-layer/components/DiscoverNavbarSearch.vue'))

  const SEARCHABLE_COURSE_ROUTE_NAMES = new Set([
    'clientportal-courses-my-courses',
    'courses',
    'courses-my-library',
    'courses-products-library',
    'courses-search',
    'my-library',
  ])

  const { t } = useI18n()

  const betaPreviewBannerMessage = computed(() => t('clientportal.betaPreview.bannerMessage'))

  const coursesAdminPreviewBannerMessage = computed(() => t('clientportal.adminPreview.bannerMessage'))

  const authStore = useAuth().store

  const { toggle: toggleSidebarDrawer } = useSidebarDrawer()

  // Freeze the chrome (sidebar + top bar) in admin-preview builder mode; the previewed theme itself stays interactive.
  const { isActive: isAdminPreviewActive, builderMode: isAdminPreviewBuilderMode } = useCoursesAdminPreview()

  const isAdminPreviewBuilder = computed(() => Boolean(isAdminPreviewActive.value && isAdminPreviewBuilderMode.value))

  const {
    route,
    isMobile,
    isAuthPage,
    isAccountSettingsPage,
    isEventDetailPage,
    isExhibitorCenterPage,
    isNonCPNavigationRoute,
    isCommunityLayerPage,
    currentGroupSlug,
    selectedProductId,
  } = useCPRouteContext()

  const visibility = useCPRootVisibility(isNonCPNavigationRoute, isMobile, isAuthPage, isAccountSettingsPage, isExhibitorCenterPage)

  const { showPortalSidebar, showSidebarToggleBar, showPortalMobileDrawer, showTopBar, showMobileTopBar } = visibility

  const { portalName, portalIcon, portalSubtitle } = useCPSwitcherSidebar(currentGroupSlug, selectedProductId)

  const { isCollapsed, toggleSidebar, navigationGroups, navigationGroupsMobile, selectedItem, currentPageTitle } = usePortalSidebar()

  // Pass only the route fields the chat shell needs, and coerce the route name
  // into a stable string|null shape so its visibility and navigation checks stay predictable.
  const chatShellRoute = {
    get path() {
      return route.path
    },
    get name() {
      return typeof route.name === 'string' ? route.name : null
    },
  }

  const chatContainerRef = ref<{ handleChatClick: () => Promise<void> } | null>(null)

  const { chatUnreadCount, handleTopBarChatClick, showChatButton, shouldMountChatContainer } = useCPChatShell(
    chatShellRoute,
    chatContainerRef
  )

  const { locationId, store: locationStore } = useLocation()

  const { searchContext } = useActiveSearchContext()

  const { hasAdminAccess, isSettled: isExhibitorAdminSettled } = useExhibitorAdminContext()

  const isCommunitiesGroupRoute = computed(() => route.name?.toString().startsWith('communities-groups-') ?? false)

  const isCoursesRoute = computed(() => (isNonCPNavigationRoute.value && route.path?.toString().startsWith('/courses/')) ?? false)

  // Mobile "Open app" banner for the location's branded app. Hidden where the page owns a
  // fixed bottom action bar (account-settings save bars, course quiz/assignment footers).
  const {
    storeLinks: appStoreLinks,
    logoSrc: appBannerLogo,
    shouldShow: showAppOpenBanner,
  } = useCPAppOpenBanner(computed(() => isAccountSettingsPage.value || isCoursesRoute.value))

  const { isVisible: isAppOpenBannerVisible } = useAppOpenBanner()

  // Reserve the banner's height in <main> (as GoKollab does) so h-full pages like mobile chat keep
  // their bottom controls above it. Auth pages are viewport-height (100dvh) and would scroll, so the
  // banner overlays them instead.
  const reserveAppBannerSpace = computed(() => isAppOpenBannerVisible.value && !isAuthPage.value)

  /** Live events directory — drives the in-place events search in the top bar center slot. */
  const isOnEventsDirectory = computed(() => isEventsDirectoryRoute(route))

  /** Any standalone `/courses` path, including transactional routes. */
  const isAnyCoursesPath = computed(() => {
    const path = route.path?.toString() ?? ''
    return path === '/courses' || path.startsWith('/courses/')
  })

  /** Browse, library, search, and course-content routes where catalog search is appropriate. */
  const isSearchableCoursesRoute = computed(() => {
    const name = route.name?.toString() ?? ''
    const path = route.path?.toString() ?? ''
    if (isCPPublicCheckoutRoutePath(path)) return false

    return (
      SEARCHABLE_COURSE_ROUTE_NAMES.has(name) ||
      name.startsWith('courses-products') ||
      path === '/courses' ||
      path === '/courses/my-courses' ||
      path === '/courses/search' ||
      path.startsWith('/courses/my-library') ||
      path.startsWith('/courses/products/') ||
      path.startsWith('/courses/upsell-offers/')
    )
  })

  const showDesktopCoursesSearch = computed(() => authStore.isLoggedIn && isSearchableCoursesRoute.value)
  const showDesktopEventsSearch = computed(() => authStore.isLoggedIn && isOnEventsDirectory.value)
  const showExhibitorCenterToggle = computed(() => (isEventDetailPage.value || isExhibitorCenterPage.value) && hasAdminAccess.value)
  /**
   * Phones have no top-bar centre slot for the Event/Exhibitor centre toggle, so admins get a row at the
   * bottom of the nav drawer instead. Event pages only: goToCenter needs the route's eventId, and the
   * drawer is not rendered on the centre itself (useCPRootVisibility), so the row is a one-way entry.
   * Gated on isSettled so it does not flash in before memberships resolve.
   */
  const showExhibitorCenterMobileEntry = computed(
    () => isMobile.value && isEventDetailPage.value && isExhibitorAdminSettled.value && hasAdminAccess.value
  )

  const isDiscoverRoute = computed(() => {
    const path = route.path?.toString() ?? ''
    return path === '/discover' || path.startsWith('/discover/')
  })

  /**
   * Discover filters in place off `?q=`, so its search belongs in the shared top bar on desktop too
   * — previously only `TopBarMobile` consumed the Discover search context.
   */
  const showDesktopDiscoverSearch = computed(() => authStore.isLoggedIn && isDiscoverRoute.value)

  const showDesktopShellSearch = computed(
    () => showDesktopCoursesSearch.value || showDesktopEventsSearch.value || showDesktopDiscoverSearch.value
  )

  const mobileShowSearch = computed(() =>
    isAnyCoursesPath.value
      ? authStore.isLoggedIn && isSearchableCoursesRoute.value
      : (searchContext.value?.shouldShowSearch?.value ?? false)
  )

  const mobileInitialSearchQuery = computed(() => searchContext.value?.initialQuery?.value ?? '')

  const handleMobileSearchOpen = () => {
    searchContext.value?.onSearchOpen?.()
  }

  const handleMobileSearchInputChange = (value: string) => {
    searchContext.value?.onInputChange?.(value)
  }

  const handleMobileSearchSubmit = (query: string) => {
    searchContext.value?.onSubmit(query)
  }

  const handleMobileSearchClose = () => {
    searchContext.value?.onSearchClose?.()
  }

  /** Resolved MongoDB _id for the active group; preferred over slug for SearchWidget cache keying. */
  const currentGroupId = computed(() => communitiesGroupBreadcrumb.value?.groupId)

  const effectiveSidebarCollapsed = computed(
    () => isNonCPNavigationRoute.value || isAccountSettingsPage.value || isExhibitorCenterPage.value || isCollapsed.value
  )

  const onSignup = () => {
    authStore.openLoginModal('SIGN_UP')
  }

  const onLogin = () => {
    if (locationStore.locationData?.defaultLoginPage === 'SECURE_CODE') {
      authStore.openLoginModal(Screens.LOGIN_WITH_SECURE_CODE)
    } else {
      authStore.openLoginModal()
    }
  }

  /**
   * Mobile top-bar center text.
   * - Account settings → portal name (branding context)
   * - Community/course routes → last breadcrumb value (group name or course name)
   * - CP shell routes → current page title
   */
  const topBarTitle = computed(() => {
    if (isAccountSettingsPage.value) return portalName.value
    if (isNonCPNavigationRoute.value) {
      const groupName = communitiesGroupBreadcrumb.value?.groupName
      if (groupName) return groupName
      const courseName = coursesProductBreadcrumb.value?.courseName
      if (courseName) return courseName
      return portalName.value
    }
    return currentPageTitle.value
  })

  const { validVisibleTabIds } = useAccountSettingsTabVisibility()

  /**
   * Shell pages set `meta.cpRoute`; communities/courses layer pages often do not, but we still want
   * breadcrumbs when resolveCpBreadcrumbRegistryKey matches (hybrid registry + path rules).
   * Gated on desktop TopBar only — CPBreadcrumb is not rendered in TopBarMobile, so enabling on
   * mobile would run queries (e.g. course title) with no visible trail.
   */
  const breadcrumbsEnabled = computed(() => {
    if (isAuthPage.value) return false
    if (!showTopBar.value) return false
    if (!isNonCPNavigationRoute.value) return true
    return resolveCpBreadcrumbRegistryKey(route) !== null
  })

  /**
   * Account settings last breadcrumb segment is driven by `?activeTab=<tab>`.
   * Tab key → i18n key mirrors AccountSettingsSwitcherRail (`account.web.sidebar.<tab_underscored>`).
   * Unknown query values fall back to profile to stay in sync with AccountSettingsSwitcherRail
   * and AccountSettingsContent, which apply the same fallback before rendering.
   */
  const accountSettingsTabLabel = computed(() => {
    if (!isAccountSettingsPage.value) return undefined
    // [].flat()[0] normalizes string | string[] | undefined → first string value (Vue Router repeats keys as string[])
    const raw = [route.query.activeTab].flat()[0] ?? ''
    const tab = validVisibleTabIds.value.includes(raw as AccountSettingsTabKey) ? raw : AccountSettingsTabKey.Profile
    return t(`account.web.sidebar.${tab.replace(/-/g, '_')}`)
  })

  const communitiesGroupBreadcrumb = useCPCommunitiesGroupBreadcrumbContext()

  const coursesProductBreadcrumb = useCPCoursesProductBreadcrumbContext()
  const eventsDetailBreadcrumbLabel = useEventsDetailBreadcrumbLabel()
  const lastSegmentLabel = computed(() => accountSettingsTabLabel.value ?? eventsDetailBreadcrumbLabel.value)

  const { breadcrumbs, breadcrumbId, separator } = useCPBreadcrumbs({
    enabled: breadcrumbsEnabled,
    communitiesGroup: communitiesGroupBreadcrumb,
    coursesProduct: coursesProductBreadcrumb,
    lastSegmentLabel,
  })

  const showBreadcrumbTrail = computed(() => Boolean(breadcrumbs.value?.length))

  /** Mobile account settings with `?activeTab=` — section sub-header only; hide portal TopBarMobile. */
  const isAccountSettingsMobileSection = computed(() => {
    if (!isAccountSettingsPage.value || !isMobile.value) {
      return false
    }
    const activeTab = [route.query.activeTab].flat()[0]
    return typeof activeTab === 'string' && activeTab.length > 0
  })

  /** Exhibitor center owns its own compact header (back + title + booth); no portal TopBarMobile, no hamburger. */
  const isExhibitorCenterMobile = computed(() => isExhibitorCenterPage.value && isMobile.value)

  const showShellMobileTopBar = computed(
    () => showMobileTopBar.value && !isAccountSettingsMobileSection.value && !isExhibitorCenterMobile.value
  )

  // Brand defaults every `useSeo()` call below this root reads (site name, canonical origin,
  // default OG image, noindex-by-default robots). Passed as a getter so the profile re-resolves
  // if portal settings land on the client tick instead of during SSR. Capture the location store
  // in setup — Unhead may re-run this getter after getActivePinia() is gone. Without this provide,
  // `useSeo` logs an error and falls back to generic "Client Portal" defaults.
  provideSeoSiteProfile(() => createClientPortalSeoSiteProfile(locationStore))

  useNotificationItemActionRegistry()

  // Owns SSR-safe favicon <link> and `{Portal} | {Page}` titleTemplate. Flips
  // store.brandedHeadOwnsDocument so applyBranding's legacy imperative writes
  // become no-ops on clientportal — Vue head is the single writer here.
  // Note: this is the single owner of the `<title>` brand suffix — the SEO profile's
  // `formatTitle` deliberately returns the bare page title so the two do not stack.
  useBrandedHead()

  // Generates the full CP theme (scales + HighRise bridge) from portal brand colours
  // (brandColor1→primary, brandColor2→secondary) and re-asserts it on non-community
  // routes. This owns the primary/secondary token scales; applyBranding still runs for
  // the favicon/title path (and redundantly re-writes --cp-brand-secondary to the same value).
  usePortalTheme()

  useClientPortalAuthStateWatcher()

  // Inject portal-level custom CSS / JS / tracking code when liveMode is enabled
  useCPCustomCode()

  // Inject communities group-level custom CSS / JS / tracking code on group routes.
  useGroupCustomCode()

  // Inject course-level custom CSS / JS / tracking code on course routes.
  useCourseCustomCode()

  /** Third-party support chat (LiveChat) from portal `chatWidgetSettings`; client-only. */
  useCPSupportChatWidget()

  useCPJoinedGroupsListPrefetch(isAuthPage)
</script>

<template>
  <HLContentWrap :full-screen="true" namespace="membershipApp" :notification-config="HIGHRISE_NOTIFICATION_CONFIG">
    <!-- Global chat-failure toasts. Must stay inside HLContentWrap: this container's setup runs above the provider. -->
    <ChatErrorToaster />

    <div class="cp-root-shell">
      <BetaPreviewBanner :message="betaPreviewBannerMessage" />
      <CoursesAdminPreviewBanner :message="coursesAdminPreviewBannerMessage" />
      <!-- Anchor element for communities group-level custom header tracking code injection -->
      <div id="communities-siteCustomHeader" />
      <!-- Anchor elements for course-level custom header tracking code injection -->
      <div id="courses-siteCustomHeader" />
      <div id="productCustomHeader" />
      <!-- Anchor element for portal-level custom header tracking code injection -->
      <div id="cp-siteCustomHeader" />
      <div
        id="cp-root-container"
        class="cp-root-container"
        :class="{
          'sidebar-collapsed': effectiveSidebarCollapsed,
          'auth-page': isAuthPage,
          'account-settings-page': isAccountSettingsPage,
          'exhibitor-center-page': isExhibitorCenterPage,
          'switcher-sidebar-mode': isCommunityLayerPage,
          'account-settings-mobile-section': isAccountSettingsMobileSection,
        }"
      >
        <CPPortalSidebar
          :portal-icon="portalIcon"
          :portal-name="portalName"
          :portal-subtitle="portalSubtitle"
          :navigation-groups="navigationGroups"
          :selected-item="selectedItem"
          :is-collapsed="effectiveSidebarCollapsed"
          :is-visible="showPortalSidebar"
          @toggle="toggleSidebar"
        />

        <!-- Content area -->
        <div class="cp-content-area" :class="{ 'cp-content-area--mobile-bg': isMobile }">
          <CPTooltip v-if="showSidebarToggleBar" placement="right" variant="dark">
            <template #trigger>
              <div
                :id="createElementId('sidebar', 'toggle', 'button')"
                class="sidebar-toggle-btn cursor-pointer"
                :class="{ 'sidebar-collapsed': isCollapsed }"
                role="button"
                tabindex="0"
                :aria-label="isCollapsed ? t('clientportal.sidebar.expandSidebar') : t('clientportal.sidebar.collapseSidebar')"
                :title="isCollapsed ? t('clientportal.sidebar.expandSidebar') : t('clientportal.sidebar.collapseSidebar')"
                @click="toggleSidebar"
                @keydown.enter="toggleSidebar"
                @keydown.space.prevent="toggleSidebar"
              >
                <span class="sidebar-toggle-inner">
                  <span class="toggle-line"></span>
                  <span class="sidebar-toggle-icons">
                    <ChevronLeftIcon v-if="!isCollapsed" class="sidebar-toggle-icon" />
                    <ChevronRightIcon v-else class="sidebar-toggle-icon" />
                  </span>
                </span>
              </div>
            </template>
            <div class="px-2 py-1 text-sm">
              {{ isCollapsed ? t('clientportal.sidebar.expandSidebar') : t('clientportal.sidebar.collapseSidebar') }}
            </div>
          </CPTooltip>

          <TopBar
            v-if="showTopBar"
            :class="['cp-topbar', isAdminPreviewBuilder ? 'pointer-events-none select-none' : '']"
            :aria-disabled="isAdminPreviewBuilder"
            :tabindex="isAdminPreviewBuilder ? -1 : undefined"
            :show-bottom-border="true"
            :compact-left-section="true"
            :show-chat-button="showChatButton"
            :chat-unread-count="chatUnreadCount"
            :wide-center-section="showDesktopShellSearch"
            @loginClick="onLogin"
            @signupClick="onSignup"
            @chatClick="handleTopBarChatClick"
          >
            <template #page-title>
              <CPBreadcrumb
                v-if="showBreadcrumbTrail && breadcrumbs"
                :id="breadcrumbId"
                :breadcrumbs="breadcrumbs"
                :home="false"
                :separator="separator"
              />
              <CPText v-else-if="topBarTitle" size="lg" weight="semibold" class="text-body">
                {{ topBarTitle }}
              </CPText>
            </template>
            <template #announcements>
              <AnnouncementsTopBarButton
                v-if="authStore.isLoggedIn"
                :button-id="createElementId('topbar', 'desktop', 'announcements-button')"
                :badge-id="createElementId('topbar', 'desktop', 'announcements-unread-badge')"
                button-class="flex-shrink-0 !h-9 !w-9 !min-w-0 !rounded-lg !p-0 transition-colors duration-200 hover:!bg-hover"
              />
            </template>
            <!-- Desktop: catalog dropdown on searchable course routes (incl. My Courses), plain filter input on Discover, events search on the live events directory. -->
            <template #search>
              <ClientOnly>
                <MyCoursesNavbarSearch v-if="showDesktopCoursesSearch" class="w-full" />
                <DiscoverNavbarSearch v-else-if="showDesktopDiscoverSearch" class="w-full" />
                <EventsDirectoryNavbarSearch v-else-if="showDesktopEventsSearch" class="w-full" />
                <ExhibitorCenterTopBarToggle v-else-if="showExhibitorCenterToggle" />
                <SearchWidget
                  v-else-if="authStore.isLoggedIn && locationId && isCommunitiesGroupRoute && isNonCPNavigationRoute"
                  :location-id="locationId"
                  :group-id="currentGroupId"
                  :placeholder="t('clientportal.search.placeholder')"
                  size="sm"
                  :use-query-params="true"
                />
              </ClientOnly>
            </template>
          </TopBar>

          <header
            v-else-if="showShellMobileTopBar"
            id="cp-mobile-topbar"
            :class="['cp-topbar', isAdminPreviewBuilder ? 'pointer-events-none select-none' : '']"
            :aria-disabled="isAdminPreviewBuilder"
            :tabindex="isAdminPreviewBuilder ? -1 : undefined"
          >
            <!-- Account settings: dedicated bar (portal + home + profile); no menu hamburger / search. -->
            <TopBarMobile
              v-if="isAccountSettingsPage"
              class="h-full w-full"
              :show-bottom-border="true"
              :show-logo="Boolean(portalIcon)"
              :logo-src="portalIcon || ''"
              logo-shape="square"
              :show-search="false"
              @loginClick="onLogin"
              @signupClick="onSignup"
            >
              <template #page-title>
                <CPBreadcrumb
                  v-if="showBreadcrumbTrail && breadcrumbs"
                  :id="breadcrumbId"
                  :breadcrumbs="breadcrumbs"
                  :home="false"
                  :separator="separator"
                />
                <CPText v-else-if="topBarTitle" size="lg" weight="semibold" class="text-body">
                  {{ topBarTitle }}
                </CPText>
              </template>
              <!-- Desktop: search only on communities *group* routes. Mobile TopBar uses useActiveSearchContext (courses, my-library, etc.) — intentional product split, not an oversight. -->
              <template #search>
                <ClientOnly>
                  <SearchWidget
                    v-if="authStore.isLoggedIn && locationId && isCommunitiesGroupRoute && isNonCPNavigationRoute"
                    :location-id="locationId"
                    :group-id="currentGroupId"
                    :placeholder="t('clientportal.search.placeholder')"
                    size="sm"
                    :use-query-params="true"
                  />
                </ClientOnly>
              </template>
            </TopBarMobile>
            <TopBarMobile
              v-else
              :show-bottom-border="true"
              class="h-full w-full"
              :show-logo="false"
              :show-auth-section="true"
              :show-search="mobileShowSearch"
              :show-chat-button="showChatButton"
              :chat-unread-count="chatUnreadCount"
              :initial-search-query="mobileInitialSearchQuery"
              @loginClick="onLogin"
              @signupClick="onSignup"
              @chatClick="handleTopBarChatClick"
              @search-open="handleMobileSearchOpen"
              @search-submit="handleMobileSearchSubmit"
              @search-input-change="handleMobileSearchInputChange"
              @search-close="handleMobileSearchClose"
            >
              <template #announcements>
                <AnnouncementsTopBarButton
                  v-if="authStore.isLoggedIn"
                  :button-id="createElementId('topbar', 'mobile', 'announcements-button')"
                  :badge-id="createElementId('topbar', 'mobile', 'announcements-unread-badge')"
                />
              </template>
              <template #left>
                <CPButton
                  :id="createElementId('sidebar', 'toggle', 'button')"
                  type="ghost"
                  size="xs"
                  class="px-1.5 touch-manipulation"
                  :aria-label="t('common.ui.openMenu')"
                  @click="toggleSidebarDrawer"
                >
                  <CustomHamburgerMenuIcon class="h-5 w-5 text-body" />
                </CPButton>
              </template>
              <template #center>
                <CPText v-if="topBarTitle" size="lg" weight="semibold" class="min-w-0 truncate text-body">
                  {{ topBarTitle }}
                </CPText>
              </template>
            </TopBarMobile>
          </header>

          <ChatContainer v-if="shouldMountChatContainer" ref="chatContainerRef" :group-slug="currentGroupSlug || undefined" />

          <main
            id="cp-main-content"
            class="cp-main-content"
            :class="{ 'bg-background': isAuthPage, 'pb-[3.25rem]': reserveAppBannerSpace }"
          >
            <div
              class="cp-main-content-inner"
              :class="[
                isAccountSettingsPage || isCoursesRoute || isEventDetailPage || isExhibitorCenterPage ? '' : isMobile ? 'p-4' : 'p-6',
                {
                  'cp-main-content-inner--account-settings': isAccountSettingsPage,
                  'cp-main-content-inner--exhibitor-center': isExhibitorCenterPage,
                  'cp-main-content-inner--event-detail': isEventDetailPage,
                },
              ]"
            >
              <slot />
            </div>
          </main>
        </div>

        <!-- Mobile "Open app" banner, fixed to the viewport bottom (client-only, mobile-gated) -->
        <ClientOnly>
          <AppOpenBanner v-if="showAppOpenBanner" :store-links="appStoreLinks" :logo-src="appBannerLogo" />
        </ClientOnly>

        <CPPortalSidebarMobileDrawer
          v-if="showPortalMobileDrawer"
          drawer-id="cp-portal-sidebar-drawer"
          :drawer-width="280"
          :portal-name="portalName"
          :portal-icon="portalIcon"
          :navigation-groups="navigationGroupsMobile"
          :selected-item="selectedItem"
        >
          <template v-if="showExhibitorCenterMobileEntry" #footer>
            <ExhibitorCenterMobileNavEntry />
          </template>
        </CPPortalSidebarMobileDrawer>

        <LoginModal />
      </div>
      <!-- Anchor element for portal-level custom footer tracking code injection -->
      <div id="cp-siteCustomFooter" />
      <!-- Anchor elements for course-level custom footer tracking code injection -->
      <div id="courses-siteCustomFooter" />
      <div id="productCustomFooter" />
      <!-- Anchor element for communities group-level custom footer tracking code injection -->
      <div id="communities-siteCustomFooter" />
      <!-- Mount for portal-configured third-party support chat embed (postscribe); body teleport escapes shell overflow -->
      <Teleport to="body">
        <div id="clientportal-chatWidget" />
      </Teleport>
    </div>
  </HLContentWrap>
</template>

<style scoped>
  .cp-root-shell {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100vh;
    max-height: 100vh;
    min-height: 0;
    overflow: hidden;
  }

  .cp-root-container {
    --cp-sidebar-width: 16rem;
    --cp-topbar-height: 3.5rem;

    display: grid;
    grid-template-columns: var(--cp-sidebar-width) 1fr;
    /* minmax(0,1fr) + child min-h-0: grid items may shrink so sidebar nav + main scroll instead of growing past the viewport */
    grid-template-rows: minmax(0, 1fr);
    flex: 1 1 0%;
    min-height: 0;
    width: 100%;
    overflow: hidden;
    transition: grid-template-columns 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .cp-root-container > * {
    min-height: 0;
  }

  .cp-root-container.sidebar-collapsed {
    --cp-sidebar-width: 3rem;
  }

  .cp-root-container.auth-page {
    grid-template-columns: 1fr;
    transition: none;
  }

  .cp-root-container.auth-page .cp-content-area {
    grid-column: 1;
  }

  .cp-root-container.switcher-sidebar-mode .cp-main-content-inner,
  .cp-root-container.auth-page .cp-main-content-inner {
    padding: 0;
    max-width: 100%;
  }

  /*
   * Communities/courses layouts (.communities-layout) use height:100% + scroll on .col-main.
   * Default inner uses flex-shrink:0 so cp-main-content scrolls; that breaks the nested chain.
   * Constrain inner height and defer scroll to the layer layout (e.g. communities-groups.vue).
   */
  .cp-root-container.switcher-sidebar-mode .cp-main-content {
    overflow: hidden;
  }

  .cp-root-container.switcher-sidebar-mode .cp-main-content-inner {
    flex: 1 1 0;
    min-height: 0;
    overflow: hidden;
  }

  /* Account settings: TopBarMobile uses h-13 (3.25rem); match shell height so no surface gap under the bar. */
  .cp-root-container.account-settings-page {
    --cp-topbar-height: 3.25rem;
  }

  /* Exhibitor center owns its compact header; reserve zero portal top-bar height on phone. */
  @media (max-width: 767px) {
    .cp-root-container.exhibitor-center-page {
      --cp-topbar-height: 0px;
    }
  }

  .cp-root-container.account-settings-page .cp-content-area {
    background-color: var(--cp-background-500);
  }

  .cp-root-container.account-settings-page .cp-main-content {
    overflow: hidden;
  }

  /* Account settings uses the dashboard shell. The inner account-settings.web.vue grid manages its own spacing, so zero the inner padding. */
  .cp-root-container.account-settings-page .cp-main-content-inner {
    flex: 1 1 0;
    min-height: 0;
    max-width: 100%;
    overflow: hidden;
  }

  /*
   * Exhibitor center owns its own two-column grid (ExhibitorCenterShell), so the shell only has to
   * hand it a full-height, unpadded box and stop scrolling itself. Same contract as account settings
   * above. Everything below the page root — including the height chain through the SDUI renderer's
   * wrapper elements — belongs to the page, not to this container.
   */
  .cp-root-container.exhibitor-center-page .cp-main-content {
    overflow: hidden;
  }

  .cp-root-container.exhibitor-center-page .cp-main-content-inner {
    flex: 1 1 0;
    min-height: 0;
    max-width: 100%;
    overflow: hidden;
  }

  .cp-main-content-inner.cp-main-content-inner--account-settings,
  .cp-main-content-inner.cp-main-content-inner--event-detail,
  .cp-main-content-inner.cp-main-content-inner--exhibitor-center {
    padding: 0;
    max-width: 100%;
  }

  .cp-root-container.sidebar-collapsed .cp-sidebar {
    width: var(--cp-sidebar-width);
  }

  .cp-content-area {
    grid-column: 2;
    grid-row: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
    position: relative;
    background-color: var(--cp-surface-500);
  }

  /* Mobile shell uses the page background instead of the surface tint. */
  .cp-content-area--mobile-bg {
    background-color: var(--cp-background-500);
  }

  .cp-topbar {
    flex-shrink: 0;
    height: var(--cp-topbar-height);
    width: 100%;
    z-index: 40;
  }

  /* Outer <main>: full width of the column so the vertical scrollbar sits at the viewport edge of the content area. */
  .cp-main-content {
    flex: 1;
    min-height: 0;
    overflow: auto;
    width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
  }

  .cp-main-content-inner {
    flex: 1 0 auto;
    width: 100%;
    max-width: 1730px;
    margin-left: auto;
    margin-right: auto;
    box-sizing: border-box;
    display: flex;
    flex-flow: column nowrap;
    min-height: 0;
  }

  .sidebar-toggle-btn {
    position: absolute;
    left: 0;
    top: 0;
    z-index: 30;
    width: 1rem;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .sidebar-toggle-inner {
    position: relative;
    width: 1rem;
    height: 1rem;
  }

  .toggle-line {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 0.125rem;
    height: 0.75rem;
    background-color: var(--cp-font-700);
    border-radius: 0.03125rem;
    opacity: 1;
    transition: opacity 0.2s ease;
  }

  .sidebar-toggle-btn:hover .toggle-line {
    opacity: 0;
  }

  .sidebar-toggle-icons {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .sidebar-toggle-btn:hover .sidebar-toggle-icons {
    opacity: 1;
  }

  .sidebar-toggle-icon {
    width: 1rem;
    height: 1rem;
    color: var(--cp-font-700);
    transition: color 0.2s ease;
  }

  .sidebar-toggle-btn:hover .sidebar-toggle-icon {
    color: var(--cp-primary-500);
  }

  .sidebar-toggle-btn:hover {
    border-left: 1px solid var(--cp-border-500);
    box-sizing: border-box;
  }

  @media (max-width: 767px) {
    .cp-root-container {
      --cp-sidebar-width: 0px;
      grid-template-columns: 1fr;
    }

    .cp-sidebar {
      display: none;
    }

    .cp-content-area {
      grid-column: 1;
    }
  }
</style>
